import React from 'react';
import Card from '../../ui/Card';
import { CheckCircleIcon, ClockIcon, AlertCircleIcon, CircleIcon } from 'lucide-react';
const LaunchMilestoneTimeline: React.FC = () => {
  const products = [{
    name: 'LED Headlamp Pro',
    milestones: [{
      stage: 'Concept',
      status: 'completed',
      date: 'Jan 2024',
      progress: 100
    }, {
      stage: 'Testing',
      status: 'completed',
      date: 'Mar 2024',
      progress: 100
    }, {
      stage: 'SOP',
      status: 'completed',
      date: 'Jun 2024',
      progress: 100
    }, {
      stage: 'GTM',
      status: 'in-progress',
      date: 'Oct 2024',
      progress: 75
    }],
    overallProgress: 94,
    launchDate: 'Oct 2024'
  }, {
    name: 'Smart Mirror System',
    milestones: [{
      stage: 'Concept',
      status: 'completed',
      date: 'Feb 2024',
      progress: 100
    }, {
      stage: 'Testing',
      status: 'completed',
      date: 'May 2024',
      progress: 100
    }, {
      stage: 'SOP',
      status: 'in-progress',
      date: 'Nov 2024',
      progress: 82
    }, {
      stage: 'GTM',
      status: 'pending',
      date: 'Jan 2025',
      progress: 0
    }],
    overallProgress: 71,
    launchDate: 'Jan 2025'
  }, {
    name: 'EV Battery Pack',
    milestones: [{
      stage: 'Concept',
      status: 'completed',
      date: 'Mar 2024',
      progress: 100
    }, {
      stage: 'Testing',
      status: 'in-progress',
      date: 'Aug 2024',
      progress: 65
    }, {
      stage: 'SOP',
      status: 'pending',
      date: 'Feb 2025',
      progress: 0
    }, {
      stage: 'GTM',
      status: 'pending',
      date: 'Apr 2025',
      progress: 0
    }],
    overallProgress: 41,
    launchDate: 'Apr 2025'
  }, {
    name: 'ADAS Sensor Suite',
    milestones: [{
      stage: 'Concept',
      status: 'completed',
      date: 'Apr 2024',
      progress: 100
    }, {
      stage: 'Testing',
      status: 'at-risk',
      date: 'Sep 2024',
      progress: 48
    }, {
      stage: 'SOP',
      status: 'pending',
      date: 'Mar 2025',
      progress: 0
    }, {
      stage: 'GTM',
      status: 'pending',
      date: 'May 2025',
      progress: 0
    }],
    overallProgress: 37,
    launchDate: 'May 2025'
  }];
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon size={20} className="text-green-600" data-id="element-7633" />;
      case 'in-progress':
        return <ClockIcon size={20} className="text-blue-600" data-id="element-7634" />;
      case 'at-risk':
        return <AlertCircleIcon size={20} className="text-red-600" data-id="element-7635" />;
      case 'pending':
        return <CircleIcon size={20} className="text-gray-300" data-id="element-7636" />;
      default:
        return null;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'in-progress':
        return 'bg-blue-500';
      case 'at-risk':
        return 'bg-red-500';
      case 'pending':
        return 'bg-gray-300';
      default:
        return 'bg-gray-300';
    }
  };
  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'text-green-600';
    if (progress >= 50) return 'text-blue-600';
    if (progress >= 30) return 'text-yellow-600';
    return 'text-red-600';
  };
  return <Card className="p-6 mb-8" data-id="element-7637">
      <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-7638">
        Launch Milestone Timeline
      </h3>
      <p className="text-sm text-gray-600 mb-6" data-id="element-7639">
        Product journey from Concept → Testing → SOP → Go-to-Market
      </p>
      <div className="space-y-8" data-id="element-7640">
        {products.map((product, productIndex) => <div key={productIndex} className="relative" data-id="element-7641">
            <div className="flex items-center justify-between mb-4" data-id="element-7642">
              <div data-id="element-7643">
                <h4 className="text-base font-bold text-gray-900" data-id="element-7644">
                  {product.name}
                </h4>
                <p className="text-xs text-gray-600" data-id="element-7645">
                  Target Launch: {product.launchDate}
                </p>
              </div>
              <div className="text-right" data-id="element-7646">
                <div className={`text-2xl font-bold ${getProgressColor(product.overallProgress)}`} data-id="element-7647">
                  {product.overallProgress}%
                </div>
                <div className="text-xs text-gray-600" data-id="element-7648">Overall Progress</div>
              </div>
            </div>
            <div className="relative" data-id="element-7649">
              {/* Timeline Line */}
              <div className="absolute top-10 left-0 right-0 h-1 bg-gray-200" data-id="element-7650" />
              {/* Milestones */}
              <div className="grid grid-cols-4 gap-4 relative" data-id="element-7651">
                {product.milestones.map((milestone, milestoneIndex) => <div key={milestoneIndex} className="relative" data-id="element-7652">
                    <div className="flex flex-col items-center" data-id="element-7653">
                      {/* Icon */}
                      <div className="relative z-10 bg-white p-2" data-id="element-7654">
                        {getStatusIcon(milestone.status)}
                      </div>
                      {/* Stage Name */}
                      <div className="text-sm font-semibold text-gray-800 mt-2" data-id="element-7655">
                        {milestone.stage}
                      </div>
                      {/* Date */}
                      <div className="text-xs text-gray-600 mt-1" data-id="element-7656">
                        {milestone.date}
                      </div>
                      {/* Progress Bar */}
                      <div className="w-full mt-3" data-id="element-7657">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-7658">
                          <div className={`h-full ${getStatusColor(milestone.status)} transition-all duration-500`} style={{
                      width: `${milestone.progress}%`
                    }} data-id="element-7659" />
                        </div>
                        <div className="text-xs text-center text-gray-600 mt-1" data-id="element-7660">
                          {milestone.progress}%
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>)}
      </div>
      <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-7661">
        <div className="grid grid-cols-4 gap-4" data-id="element-7662">
          <div className="flex items-center justify-center" data-id="element-7663">
            <CheckCircleIcon size={16} className="text-green-600 mr-2" data-id="element-7664" />
            <div className="text-sm" data-id="element-7665">
              <div className="font-semibold text-gray-900" data-id="element-7666">Completed</div>
              <div className="text-xs text-gray-600" data-id="element-7667">100% done</div>
            </div>
          </div>
          <div className="flex items-center justify-center" data-id="element-7668">
            <ClockIcon size={16} className="text-blue-600 mr-2" data-id="element-7669" />
            <div className="text-sm" data-id="element-7670">
              <div className="font-semibold text-gray-900" data-id="element-7671">In Progress</div>
              <div className="text-xs text-gray-600" data-id="element-7672">Active work</div>
            </div>
          </div>
          <div className="flex items-center justify-center" data-id="element-7673">
            <AlertCircleIcon size={16} className="text-red-600 mr-2" data-id="element-7674" />
            <div className="text-sm" data-id="element-7675">
              <div className="font-semibold text-gray-900" data-id="element-7676">At Risk</div>
              <div className="text-xs text-gray-600" data-id="element-7677">Needs attention</div>
            </div>
          </div>
          <div className="flex items-center justify-center" data-id="element-7678">
            <CircleIcon size={16} className="text-gray-300 mr-2" data-id="element-7679" />
            <div className="text-sm" data-id="element-7680">
              <div className="font-semibold text-gray-900" data-id="element-7681">Pending</div>
              <div className="text-xs text-gray-600" data-id="element-7682">Not started</div>
            </div>
          </div>
        </div>
      </div>
    </Card>;
};
export default LaunchMilestoneTimeline;